import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type { ExamSearchFilters } from '../exam-search-filters';

export interface UseExamSearchInput {
  filters: ExamSearchFilters;
  page: number;
  perPage: number;
  sortDirection: 'asc' | 'desc';
  /**
   * `false` enquanto a tela ainda confere os filtros da URL: sem isso, uma
   * instituição de outra UF iria ao back antes de a lista de instituições
   * chegar para descartá-la.
   */
  enabled?: boolean;
}

/**
 * A busca de provas (DESEN-41): só prova real e publicada, porque rascunho
 * não aparece em lugar nenhum e o simulado não é prova que alguém procura por
 * caderno. Mantém a página anterior enquanto a nova carrega.
 */
export function useExamSearch(input: UseExamSearchInput) {
  const repository = container.resolve(ContentRepositoryToken);
  const { filters } = input;
  return useQuery({
    queryKey: [
      'content',
      'items',
      'exams',
      'search',
      filters,
      input.page,
      input.perPage,
      input.sortDirection,
    ],
    queryFn: () =>
      repository.listContentItems({
        page: input.page,
        perPage: input.perPage,
        status: 'PUBLISHED',
        examKind: 'REAL_EXAM',
        uf: filters.uf || undefined,
        institutionId: filters.institutionId || undefined,
        year: filters.year ? Number(filters.year) : undefined,
        variant: filters.variant || undefined,
        sortBy: 'createdAt',
        sortDirection: input.sortDirection,
      }),
    placeholderData: keepPreviousData,
    enabled: input.enabled ?? true,
  });
}

/**
 * Os anos e os cadernos da instituição (os cadernos só do ano, quando ele
 * vem). Sem instituição não há opção a pedir: Ano e Caderno dependem dela.
 */
export function useExamFilterOptions(institutionId: string, year: string) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'exam-filter-options', institutionId, year],
    queryFn: () =>
      repository.getExamFilterOptions(
        institutionId,
        year ? Number(year) : undefined,
      ),
    enabled: institutionId !== '',
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}
